import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { isSignedIn, type AuthState, type PsnVerificationState } from '@plathunt/types';
import { useAuth } from '../app/providers.js';

/** Der Guest-Zustand ist kein Fehler, sondern ein eigener Screen. */
function GuestProfile() {
  return (
    <section>
      <p className="ph-auth__kicker">Profil</p>
      <h1 className="ph-screen__title">Du bist als Gast unterwegs</h1>
      <p className="ph-screen__note">
        Alles funktioniert auch ohne Konto – dein Fortschritt bleibt dann lokal auf diesem Gerät.
        Mit einem Konto wird er synchronisiert.
      </p>

      <div className="ph-card">
        <div className="ph-stack">
          <Link className="ph-button" to="/login">
            Anmelden
          </Link>
          <Link className="ph-button ph-button--ghost" to="/register">
            Account erstellen
          </Link>
        </div>
      </div>

      <div className="ph-card">
        <div className="ph-section__head">
          <h2 className="ph-section__title ph-section__title--psn">PlayStation Network</h2>
          <span className="ph-badge ph-badge--off">nicht verbunden</span>
        </div>
        <p className="ph-muted">
          Ein PSN-Konto lässt sich erst nach der Anmeldung verknüpfen. PlatHunt-Account und PSN
          bleiben getrennt.
        </p>
      </div>
    </section>
  );
}

const psnStatusLabel: Record<PsnVerificationState, string> = {
  unlinked: 'nicht verbunden',
  pending: 'Verifizierung offen',
  verified: 'verifiziert',
  failed: 'Verifizierung fehlgeschlagen',
};

function psnBadgeClass(status: PsnVerificationState): string {
  if (status === 'verified') return 'ph-badge ph-badge--ok';
  if (status === 'pending' || status === 'failed') return 'ph-badge ph-badge--pending';
  return 'ph-badge ph-badge--off';
}

function PsnSection({ state }: { state: AuthState }) {
  const psn = state.kind === 'psn_linked' ? state.psnAccount : null;

  return (
    <div className="ph-card">
      <div className="ph-section__head">
        <h2 className="ph-section__title ph-section__title--psn">PlayStation Network</h2>
        <span className={psnBadgeClass(psn?.verificationState ?? 'unlinked')}>
          {psnStatusLabel[psn?.verificationState ?? 'unlinked']}
        </span>
      </div>

      {psn ? (
        <>
          <div className="ph-kv">
            <span className="ph-kv__key">Online-ID</span>
            <span className="ph-kv__value">{psn.onlineId}</span>
          </div>
          <div className="ph-kv">
            <span className="ph-kv__key">Trophy-Sync</span>
            <span className="ph-kv__value">
              {psn.lastSyncedAt
                ? new Date(psn.lastSyncedAt).toLocaleDateString('de-DE')
                : 'noch nie gelaufen'}
            </span>
          </div>
          <p className="ph-todo">
            TODO: Trennen und neu verknüpfen fehlt noch – dafür gibt es serverseitig noch keinen
            Endpunkt.
          </p>
        </>
      ) : (
        <>
          <p className="ph-muted">
            Ohne verknüpftes PSN-Konto bleibt der Fortschritt manuell. Die Verknüpfung läuft später
            über einen einmaligen Code in deiner PSN-Bio.
          </p>
          <div className="ph-stack">
            <button className="ph-button ph-button--ghost" type="button" disabled>
              PSN verbinden
            </button>
          </div>
          <p className="ph-todo">
            TODO: Der Verifizierungs-Flow (Code erzeugen, Bio prüfen) ist serverseitig noch nicht
            gebaut – der Button bleibt bis dahin deaktiviert.
          </p>
        </>
      )}
    </div>
  );
}

export function Profile() {
  const { state, email, capabilities, signOut } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  if (!isSignedIn(state)) return <GuestProfile />;

  const profile = state.profile;

  async function logout() {
    setBusy(true);
    await signOut();
    setBusy(false);
    navigate('/', { replace: true });
  }

  return (
    <section>
      <p className="ph-auth__kicker">Profil</p>
      <h1 className="ph-screen__title">{profile.displayName ?? 'Dein Account'}</h1>

      <div className="ph-card">
        <div className="ph-section__head">
          <h2 className="ph-section__title ph-section__title--account">PlatHunt Account</h2>
          <span className="ph-badge ph-badge--ok">aktiv</span>
        </div>

        <div className="ph-kv">
          <span className="ph-kv__key">E-Mail</span>
          <span className="ph-kv__value">{email ?? '–'}</span>
        </div>
        <div className="ph-kv">
          <span className="ph-kv__key">Anzeigename</span>
          <span className="ph-kv__value">{profile.displayName ?? 'nicht gesetzt'}</span>
        </div>
        <div className="ph-kv">
          <span className="ph-kv__key">Fortschritt</span>
          <span className="ph-kv__value">{capabilities.cloudSync ? 'Cloud-Sync' : 'lokal'}</span>
        </div>
        <div className="ph-kv">
          <span className="ph-kv__key">Mitglied seit</span>
          <span className="ph-kv__value">
            {new Date(profile.createdAt).toLocaleDateString('de-DE')}
          </span>
        </div>
        {profile.isAdmin && (
          <div className="ph-kv">
            <span className="ph-kv__key">Rolle</span>
            <span className="ph-kv__value">
              <span className="ph-badge ph-badge--ok">Admin</span>
            </span>
          </div>
        )}

        <div className="ph-stack">
          <button
            className="ph-button ph-button--danger"
            type="button"
            onClick={logout}
            disabled={busy}
          >
            {busy ? 'Wird abgemeldet …' : 'Abmelden'}
          </button>
        </div>
        <p className="ph-todo">
          TODO: Profil bearbeiten (Anzeigename, Avatar, Theme) braucht noch den Schreibpfad im
          User-Repository.
        </p>
      </div>

      <PsnSection state={state} />
    </section>
  );
}
